import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface InventoryItem {
  id: number;
  name: string;
  environment_name: string | null;
  is_current_environment?: boolean;
  is_mobile?: boolean;
  resources: Array<{
    id: number;
    specific_name: string;
    generic_resource: string;
    condition: number;
    transportability: number;
  }>;
}

export interface InventoryData {
  inventories: Array<InventoryItem>;
  unassigned_resources: Array<{
    id: number;
    specific_name: string;
    generic_resource: string;
    condition: number;
    transportability: number;
  }>;
  current_environment_name?: string;
}

@customElement('inventory-modal')
export class InventoryModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) inventoryData: InventoryData | null = null;
  @state() private isDragging = false;
  @state() private draggedItem: any = null;

  static styles = css`
    /* Video Game Style Inventory Modal */
    .inventory-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      backdrop-filter: blur(12px);
      z-index: 2000; /* Tier 1: Standard modals (mutually exclusive with other modals) */
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }

    .inventory-modal-overlay.open {
      opacity: 1;
      pointer-events: all;
    }

    .inventory-modal {
      width: min(95vw, 800px);
      height: min(90vh, 600px);
      background: linear-gradient(145deg, #1a1a2e, #16213e);
      border-radius: 20px;
      border: 3px solid #0f3460;
      box-shadow:
        0 0 30px rgba(15, 52, 96, 0.5),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transform: scale(0.8) translateY(50px);
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      position: relative;
    }

    .inventory-modal-overlay.open .inventory-modal {
      transform: scale(1) translateY(0);
    }

    .inventory-modal-header {
      background: linear-gradient(90deg, #0f3460, #16213e);
      padding: 1.2rem;
      border-bottom: 2px solid #0f3460;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
    }

    .inventory-modal-header::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, #4fc3f7, #29b6f6, #03a9f4);
    }

    .inventory-modal-title {
      color: #e3f2fd;
      font-size: 1.6rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 0.8rem;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    }

    .inventory-modal-close {
      background: linear-gradient(145deg, #e53935, #c62828);
      border: 2px solid #b71c1c;
      color: white;
      padding: 10px 16px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      font-weight: 600;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }

    .inventory-modal-close:hover {
      background: linear-gradient(145deg, #f44336, #d32f2f);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(244, 67, 54, 0.4);
    }

    .inventory-modal-close:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    .inventory-modal-body {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      padding: 1.5rem;
      gap: 1.5rem;
    }

    /* Environment Info Section */
    .environment-info {
      background: linear-gradient(135deg, #0f3460, #16213e);
      border: 2px solid #29b6f6;
      border-radius: 16px;
      padding: 1rem;
      margin-bottom: 1rem;
      text-align: center;
    }

    /* Environment Sections */
    .environment-section {
      margin-bottom: 1.5rem;
    }

    .environment-section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.8rem;
      padding: 0.5rem 0;
      border-bottom: 1px solid rgba(41, 182, 246, 0.3);
    }

    .environment-section-header h3 {
      margin: 0;
      color: #29b6f6;
      font-size: 1.1rem;
      font-weight: 600;
    }

    .inventory-count {
      color: #81c784;
      font-size: 0.9rem;
      font-weight: 500;
    }

    .environment-name {
      color: #4fc3f7;
      font-size: 1.3rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    }

    .environment-description {
      color: #e3f2fd;
      font-size: 0.95rem;
      opacity: 0.9;
    }

    /* Inventory Grid */
    .inventory-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1.5rem;
      overflow-y: auto;
      max-height: 400px;
      padding-right: 8px;
    }

    /* Inventory Container */
    .inventory-container {
      background: linear-gradient(145deg, #1e3a8a, #1e40af);
      border: 2px solid #3b82f6;
      border-radius: 12px;
      padding: 1rem;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      margin-bottom: 1rem;
    }

    /* Environment-specific container styles */
    .inventory-container.current-environment {
      border-color: #4ade80;
      background: linear-gradient(145deg, #166534, #15803d);
    }

    .inventory-container.current-environment::before {
      background: linear-gradient(90deg, #4ade80, #22c55e, #16a34a);
    }

    .inventory-container.mobile {
      border-color: #f59e0b;
      background: linear-gradient(145deg, #92400e, #b45309);
    }

    .inventory-container.mobile::before {
      background: linear-gradient(90deg, #fbbf24, #f59e0b, #d97706);
    }

    .inventory-container.other {
      border-color: #8b5cf6;
      background: linear-gradient(145deg, #5b21b6, #6d28d9);
    }

    .inventory-container.other::before {
      background: linear-gradient(90deg, #a78bfa, #8b5cf6, #7c3aed);
    }

    .inventory-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #60a5fa, #3b82f6, #2563eb);
    }

    .inventory-container:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
      border-color: #60a5fa;
    }

    .inventory-container-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      padding-bottom: 0.8rem;
      border-bottom: 1px solid rgba(96, 165, 250, 0.3);
    }

    .inventory-container-title {
      color: #e3f2fd;
      font-size: 1.1rem;
      font-weight: 700;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }

    .inventory-container-count {
      background: linear-gradient(145deg, #10b981, #059669);
      color: white;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    /* Inventory Items */
    .inventory-items {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
      gap: 0.6rem;
      max-height: 180px;
      overflow-y: auto;
      padding: 0.5rem 0;
    }

    .inventory-item {
      background: linear-gradient(145deg, #374151, #4b5563);
      border: 1px solid #6b7280;
      border-radius: 8px;
      padding: 0.5rem 0.3rem;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;
      position: relative;
      overflow: hidden;
      min-height: 75px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .inventory-item::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, #10b981, #059669);
    }

    .inventory-item:hover {
      transform: translateY(-3px) scale(1.05);
      box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
      border-color: #10b981;
    }

    .inventory-item-icon {
      font-size: 1.8rem;
      margin-bottom: 0.3rem;
      display: block;
      flex-shrink: 0;
    }

    .inventory-item-name {
      color: #f3f4f6;
      font-size: 0.7rem;
      font-weight: 600;
      line-height: 1.1;
      text-align: center;
      word-break: break-word;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    /* Empty inventory placeholder */
    .empty-inventory-placeholder {
      grid-column: 1 / -1;
      text-align: center;
      padding: 1rem;
      color: #9ca3af;
      font-style: italic;
    }

    .empty-inventory-placeholder .empty-icon {
      font-size: 1.5rem;
      margin-bottom: 0.3rem;
      opacity: 0.6;
    }

    .empty-inventory-placeholder .empty-text {
      font-size: 0.8rem;
      opacity: 0.8;
    }

    /* Empty State */
    .empty-inventory {
      text-align: center;
      padding: 2rem;
      color: #9ca3af;
      font-style: italic;
    }

    /* Mobile Responsive */
    @media (max-width: 768px) {
      .inventory-modal {
        width: 95vw;
        height: 85vh;
        margin: 0 2.5vw;
      }

      .inventory-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
      }

      .inventory-items {
        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
        gap: 0.6rem;
      }

      .inventory-modal-title {
        font-size: 1.3rem;
      }
    }


  `;

  /**
   * Handle item click for simple interaction
   */
  private handleItemClick = (item: any) => {
    console.log('🎮 Item clicked:', item.specific_name);
    // For now, just log the click - could add item details modal later
  };

  /**
   * Get all user inventories (no filtering by environment)
   */
  private getAllUserInventories() {
    if (!this.inventoryData?.inventories) return [];
    return this.inventoryData.inventories;
  }

  /**
   * Check if user has any inventories
   */
  private hasCurrentEnvironmentInventories(): boolean {
    const allInventories = this.getAllUserInventories();
    return allInventories.length > 0;
  }

  /**
   * Group inventories by environment for visual differentiation
   */
  private getInventoriesByEnvironment() {
    const inventories = this.getAllUserInventories();
    const grouped: {
      current: InventoryItem[];
      mobile: InventoryItem[];
      other: InventoryItem[];
    } = {
      current: [],
      mobile: [],
      other: []
    };

    inventories.forEach(inv => {
      if (inv.is_current_environment) {
        grouped.current.push(inv);
      } else if (inv.environment_name === null || inv.is_mobile) {
        grouped.mobile.push(inv);
      } else {
        grouped.other.push(inv);
      }
    });

    return grouped;
  }

  /**
   * Create inventory for current environment
   */
  private createInventoryForCurrentEnvironment = () => {
    console.log('🎒 Creating inventory for current environment');
    // This would trigger a modal or API call to create a new inventory
    this.dispatchEvent(new CustomEvent('create-inventory', {
      detail: { environmentName: this.inventoryData?.current_environment_name },
      bubbles: true
    }));
  };

  /**
   * Get resource icon based on type
   */
  private getResourceIcon(genericResource: string): string {
    const iconMap: { [key: string]: string } = {
      'tool': '🔧',
      'equipment': '⚙️',
      'material': '📦',
      'book': '📚',
      'electronic': '💻',
      'furniture': '🪑',
      'clothing': '👕',
      'food': '🍎',
      'default': '📦'
    };

    const key = genericResource.toLowerCase();
    return iconMap[key] || iconMap['default'];
  }

  /**
   * Close modal
   */
  private closeModal = () => {
    this.dispatchEvent(new CustomEvent('close-modal', { bubbles: true }));
  };

  render() {
    if (!this.open) return html``;

    const inventoriesByEnvironment = this.getInventoriesByEnvironment();
    const allInventories = this.getAllUserInventories();
    const currentEnvironmentName = this.inventoryData?.current_environment_name || 'Current Location';

    return html`
      <div class="inventory-modal-overlay ${this.open ? 'open' : ''}" data-testid="inventory-modal-overlay" @click=${this.closeModal}>
        <div class="inventory-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="inventory-modal-header">
            <div class="inventory-modal-title">
              🎒 My Inventory
            </div>
            <button class="inventory-modal-close" data-testid="inventory-modal-close-btn" @click=${this.closeModal}>✕</button>
          </div>

          <div class="inventory-modal-body">
            <!-- Current Environment Info -->
            <div class="environment-info">
              <div class="environment-name">📍 ${currentEnvironmentName}</div>
              <div class="environment-description">Your available resources in this location</div>
            </div>

            <!-- Inventory Grid -->
            <div class="inventory-grid">
              ${allInventories.length > 0 ? html`
                <!-- Current Environment Inventories -->
                ${inventoriesByEnvironment.current.length > 0 ? html`
                  <div class="environment-section" data-testid="inventory-section-current">
                    <div class="environment-section-header">
                      <h3>🏠 ${currentEnvironmentName}</h3>
                      <span class="inventory-count">${inventoriesByEnvironment.current.length} inventory(ies)</span>
                    </div>
                    ${inventoriesByEnvironment.current.map((inventory: any) => html`
                      <div class="inventory-container current-environment">
                        <div class="inventory-container-header">
                          <div class="inventory-container-title">
                            🏠 ${inventory.name}
                          </div>
                          <div class="inventory-container-count">${inventory.resources?.length || 0} items</div>
                        </div>
                        <div class="inventory-items">
                          ${inventory.resources?.length > 0 ? inventory.resources.map((resource: any, idx: number) => html`
                            <div class="inventory-item" data-testid="inventory-item-${idx}" @click=${() => this.handleItemClick(resource)}>
                              <div class="inventory-item-icon">${this.getResourceIcon(resource.generic_resource)}</div>
                              <div class="inventory-item-name">${resource.specific_name}</div>
                            </div>
                          `) : html`
                            <div class="empty-inventory-placeholder" data-testid="inventory-empty-state">
                              <div class="empty-icon">📦</div>
                              <div class="empty-text">Empty</div>
                            </div>
                          `}
                        </div>
                      </div>
                    `)}
                  </div>
                ` : ''}

                <!-- Mobile Inventories -->
                ${inventoriesByEnvironment.mobile.length > 0 ? html`
                  <div class="environment-section" data-testid="inventory-section-mobile">
                    <div class="environment-section-header">
                      <h3>🎒 Mobile</h3>
                      <span class="inventory-count">${inventoriesByEnvironment.mobile.length} inventory(ies)</span>
                    </div>
                    ${inventoriesByEnvironment.mobile.map((inventory: any) => html`
                      <div class="inventory-container mobile">
                        <div class="inventory-container-header">
                          <div class="inventory-container-title">
                            🎒 ${inventory.name}
                          </div>
                          <div class="inventory-container-count">${inventory.resources?.length || 0} items</div>
                        </div>
                        <div class="inventory-items">
                          ${inventory.resources?.length > 0 ? inventory.resources.map((resource: any, idx: number) => html`
                            <div class="inventory-item" data-testid="inventory-item-${idx}" @click=${() => this.handleItemClick(resource)}>
                              <div class="inventory-item-icon">${this.getResourceIcon(resource.generic_resource)}</div>
                              <div class="inventory-item-name">${resource.specific_name}</div>
                            </div>
                          `) : html`
                            <div class="empty-inventory-placeholder" data-testid="inventory-empty-state">
                              <div class="empty-icon">📦</div>
                              <div class="empty-text">Empty</div>
                            </div>
                          `}
                        </div>
                      </div>
                    `)}
                  </div>
                ` : ''}

                <!-- Other Environment Inventories -->
                ${inventoriesByEnvironment.other.length > 0 ? html`
                  <div class="environment-section" data-testid="inventory-section-other">
                    <div class="environment-section-header">
                      <h3>🌍 Other Locations</h3>
                      <span class="inventory-count">${inventoriesByEnvironment.other.length} inventory(ies)</span>
                    </div>
                    ${inventoriesByEnvironment.other.map((inventory: any) => html`
                      <div class="inventory-container other">
                        <div class="inventory-container-header">
                          <div class="inventory-container-title">
                            🌍 ${inventory.name} (${inventory.environment_name})
                          </div>
                          <div class="inventory-container-count">${inventory.resources?.length || 0} items</div>
                        </div>
                        <div class="inventory-items">
                          ${inventory.resources?.length > 0 ? inventory.resources.map((resource: any, idx: number) => html`
                            <div class="inventory-item" data-testid="inventory-item-${idx}" @click=${() => this.handleItemClick(resource)}>
                              <div class="inventory-item-icon">${this.getResourceIcon(resource.generic_resource)}</div>
                              <div class="inventory-item-name">${resource.specific_name}</div>
                            </div>
                          `) : html`
                            <div class="empty-inventory-placeholder" data-testid="inventory-empty-state">
                              <div class="empty-icon">📦</div>
                              <div class="empty-text">Empty</div>
                            </div>
                          `}
                        </div>
                      </div>
                    `)}
                  </div>
                ` : ''}
              ` : html`
                <div class="empty-inventory" style="grid-column: 1 / -1; padding: 3rem; text-align: center;">
                  <div style="font-size: 3rem; margin-bottom: 1rem;">📦</div>
                  <div style="font-size: 1.2rem; margin-bottom: 1rem; color: #e3f2fd;">No inventories found for ${currentEnvironmentName}</div>
                  <div style="color: #cbd5e1; margin-bottom: 2rem;">You don't have any inventories set up for your current environment yet.</div>
                  <button
                    data-testid="inventory-create-btn"
                    class="action-button confirm"
                    @click=${this.createInventoryForCurrentEnvironment}
                    style="padding: 0.8rem 1.5rem; border-radius: 12px; font-weight: 600; cursor: pointer; background: linear-gradient(145deg, #10b981, #059669); color: white; border: 2px solid #047857;"
                  >
                    Create Inventory for ${currentEnvironmentName}
                  </button>
                </div>
              `}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
